import cn from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { IRowAction } from 'core-components/library/list/types'
import { Tooltip } from 'core-components/library/tooltip'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'

import { DashboardChartMeasure, DashboardGroupBy } from '../../../../model/types'
import { formatMoney } from '../../../helpers/formatMoney'
import { getListGroupLabel, getListMeasureLabel } from '../../config'
import { IListColumn } from '../../helpers/getColumns'
import { EMPTY_LIST_VALUE } from '../../helpers/listDictionaries'
import {
  IListFinancialTotals,
  IListSort,
  TGroupedInitiativeRow,
  TListGroupBy,
  TListMeasure,
} from '../../models'
import { InitiativeStatusBadge } from '../initiativeStatusBadge/InitiativeStatusBadge'

import { FinancialGoalTotals } from './FinancialGoalTotals'

import styles from './GroupedPaginatedTable.module.scss'

interface IGroupedPaginatedTableProps {
  rows: TGroupedInitiativeRow[]
  columns: IListColumn[]
  groupBy: TListGroupBy
  measure: TListMeasure
  activeCurrency: string
  sort: IListSort
  onSortChange: (sort: IListSort) => void
  initiativesCount: number
  totalMeasureValue: number | null
  financialTotals: IListFinancialTotals
  onClearFilters: () => void
  hasFilters: boolean
  rowActions?: IRowAction<TGroupedInitiativeRow>[]
  rowActionsVisible?: 'visible' | 'hidden'
  highlightedEntryIds?: ReadonlySet<number>
}

export const GroupedPaginatedTable = ({
  rows,
  columns,
  groupBy,
  measure,
  activeCurrency,
  sort,
  onSortChange,
  initiativesCount,
  totalMeasureValue,
  financialTotals,
  onClearFilters,
  hasFilters,
  rowActions = [],
  rowActionsVisible = 'hidden',
  highlightedEntryIds,
}: IGroupedPaginatedTableProps) => {
  const t = useSafetyTranslation()
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const groups = useMemo(() => rows.filter((row) => row.isGroupRow), [rows])
  const allCollapsed =
    groups.length > 0 && groups.every((group) => collapsed.has(group.rowKey ?? ''))
  const showActions = rowActions.length > 0 && rowActionsVisible === 'visible'
  const columnCount = columns.length + (showActions ? 1 : 0)
  const isCount = measure === DashboardChartMeasure.Count || measure === 'no-sales-target'
  // Give the two financial figures room without changing the individual initiative columns.
  const totalsColumnSpan = isCount ? 1 : 2
  const formatTotal = (value: number) =>
    isCount ? value.toLocaleString('en-US') : formatMoney(value, activeCurrency)
  useEffect(() => setCollapsed(new Set()), [groupBy])
  const toggleGroup = (key: string) =>
    setCollapsed((values) => {
      const result = new Set(values)
      result.has(key) ? result.delete(key) : result.add(key)
      return result
    })
  let hidden = false
  return (
    <div className={styles.root}>
      <div className={styles.summary}>
        <Translation
          text={
            groupBy === 'none'
              ? TRANSLATION_KEYS.listView.summary
              : TRANSLATION_KEYS.listView.summaryGrouped
          }
          replaceParams={[
            { key: '{count}', value: String(initiativesCount) },
            { key: '{groupCount}', value: String(groups.length) },
            { key: '{groupBy}', value: t(getListGroupLabel(groupBy)) },
          ]}
        />
        {groups.length > 0 && (
          <Button
            variant="text"
            onClick={() =>
              setCollapsed(
                allCollapsed ? new Set() : new Set(groups.map((row) => row.rowKey ?? ''))
              )
            }
          >
            <Translation
              text={
                allCollapsed
                  ? TRANSLATION_KEYS.listView.expandAll
                  : TRANSLATION_KEYS.listView.collapseAll
              }
            />
          </Button>
        )}
      </div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.field} style={{ width: column.width }} />
            ))}
            {showActions && <col className={styles.actionsColumn} />}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.field}
                  scope="col"
                  className={cn(column.className, { [styles.sorted]: sort.field === column.field })}
                  aria-sort={sort.field === column.field ? sort.direction : 'none'}
                >
                  <button
                    type="button"
                    className={styles.sortButton}
                    onClick={() =>
                      onSortChange({
                        field: column.field,
                        direction:
                          sort.field === column.field
                            ? sort.direction === 'ascending'
                              ? 'descending'
                              : 'ascending'
                            : column.field === 'finGoal'
                              ? 'descending'
                              : 'ascending',
                      })
                    }
                  >
                    {column.titleRender
                      ? column.titleRender({ column, headerLine: 1 })
                      : column.title}
                    {sort.field === column.field && (
                      <span
                        className={cn(styles.sortCaret, {
                          [styles.up]: sort.direction === 'descending',
                        })}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </th>
              ))}
              {showActions && <th className={styles.actionsColumn} scope="col" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              if (item.isGroupRow) {
                const key = item.rowKey ?? ''
                hidden = collapsed.has(key)
                const label =
                  item.groupKey === EMPTY_LIST_VALUE
                    ? groups.length === 1
                      ? TRANSLATION_KEYS.listView.summary
                      : TRANSLATION_KEYS.listView.notSpecified
                    : groupBy === DashboardGroupBy.Status
                      ? getKeyInitiativeStatusTranslationKey(item.groupKey)
                      : (item.groupKey ?? '')
                return (
                  <tr key={key} className={styles.groupRow} onClick={() => toggleGroup(key)}>
                    <td colSpan={columns.length - totalsColumnSpan}>
                      <div className={styles.groupContent}>
                        <button
                          type="button"
                          className={styles.groupToggle}
                          aria-expanded={!hidden}
                        >
                          <span
                            className={cn(styles.chevron, { [styles.collapsed]: hidden })}
                            aria-hidden="true"
                          />
                          <span className={styles.groupName}>
                            <Translation
                              text={label}
                              replaceParams={
                                label === TRANSLATION_KEYS.listView.summary
                                  ? [{ key: '{count}', value: String(item.groupItemsCount ?? 0) }]
                                  : undefined
                              }
                              span
                            />
                          </span>
                        </button>
                        <span className={styles.groupCount}>
                          <Translation
                            text={
                              item.groupItemsCount === 1
                                ? TRANSLATION_KEYS.dashboard.table.initiativesCountOne
                                : TRANSLATION_KEYS.dashboard.table.initiativesCountMany
                            }
                            replaceParams={[
                              { key: '{count}', value: String(item.groupItemsCount ?? 0) },
                            ]}
                            span
                          />
                        </span>
                        {groupBy !== DashboardGroupBy.Status && (
                          <span className={styles.statusMix}>
                            {item.groupStatusCounts
                              ?.filter((entry) => entry.status !== EMPTY_LIST_VALUE)
                              .map((entry) => (
                                <InitiativeStatusBadge
                                  key={entry.status}
                                  status={entry.status}
                                  count={entry.count}
                                  compact
                                />
                              ))}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.groupTotal} colSpan={totalsColumnSpan}>
                      {isCount ? (
                        item.groupMeasureValue != null ? (
                          formatTotal(item.groupMeasureValue)
                        ) : null
                      ) : (
                        <FinancialGoalTotals
                          totals={item.groupFinancialTotals}
                          activeCurrency={activeCurrency}
                        />
                      )}
                    </td>
                    {showActions && <td className={styles.actionsColumn} />}
                  </tr>
                )
              }
              if (hidden) return null
              return (
                <tr
                  key={item.rowKey}
                  className={cn(styles.row, {
                    [styles.copiedRow]:
                      item.EntryId != null && highlightedEntryIds?.has(item.EntryId),
                  })}
                >
                  {columns.map((column) => (
                    <td key={column.field} className={column.className}>
                      {column.renderFunction ? column.renderFunction(item, column) : null}
                    </td>
                  ))}
                  {showActions && (
                    <td className={styles.actionsColumn}>
                      <div className={styles.actions}>
                        {rowActions.map((action) => (
                          <Tooltip
                            key={action.id}
                            inline
                            tipContent={() => <Translation text={action.title ?? ''} />}
                          >
                            <Button
                              title={t(action.title ?? '')}
                              iconButton={
                                typeof action.icon === 'function' ? action.icon(item) : action.icon
                              }
                              variant="text"
                              onClick={() => action.action(item)}
                            />
                          </Tooltip>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
            {!initiativesCount && (
              <tr>
                <td colSpan={columnCount} className={styles.empty}>
                  <Translation
                    text={
                      hasFilters
                        ? TRANSLATION_KEYS.listView.emptyFiltered
                        : TRANSLATION_KEYS.listView.empty
                    }
                  />
                  {hasFilters && (
                    <Button variant="text" onClick={onClearFilters}>
                      <Translation text={TRANSLATION_KEYS.listView.clearAll} />
                    </Button>
                  )}
                </td>
              </tr>
            )}
            {initiativesCount > 0 && (
              <tr className={styles.totalRow}>
                <td colSpan={columns.length - totalsColumnSpan}>
                  <Translation
                    text={TRANSLATION_KEYS.listView.total}
                    replaceParams={[{ key: '{count}', value: String(initiativesCount) }]}
                  />
                </td>
                <td className={styles.groupTotal} colSpan={totalsColumnSpan}>
                  {!isCount ? (
                    <FinancialGoalTotals totals={financialTotals} activeCurrency={activeCurrency} />
                  ) : totalMeasureValue != null ? (
                    <>
                      <span>{formatTotal(totalMeasureValue)}</span>
                      <span className={styles.totalMeasure}>
                        <Translation text={getListMeasureLabel(measure)} span />
                      </span>
                    </>
                  ) : (
                    <span className={styles.noTarget}>-</span>
                  )}
                </td>
                {showActions && <td className={styles.actionsColumn} />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
